import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  useTheme,
  Collapse,
  IconButton,
  TableHead,
  CircularProgress,
  Alert,
  Popover,
  Button,
  TableSortLabel,
} from '@mui/material';
import { KeyboardArrowDown, KeyboardArrowUp, CalendarToday } from '@mui/icons-material';
import { DateRange } from 'react-date-range';
import moment from 'moment';
import { setDefaultOptions } from 'date-fns';
import { enUS } from 'date-fns/locale';
import { dashboardService, MainRowData, AppData } from '../api/dashboard';
import { formatLongString } from '../utils/stringUtils';
import 'react-date-range/dist/styles.css'; // main style file
import 'react-date-range/dist/theme/default.css'; // theme css file
import '../styles/dateRangePicker.css';


// Set default locale for date-fns
setDefaultOptions({ locale: enUS });

interface DateRangeState {
  startDate: Date;
  endDate: Date;
  key: string;
}

type SortField = 'landed' | 'clickOut' | 'percentage';
type SortOrder = 'asc' | 'desc';

const Dashboard = () => {
  const theme = useTheme();
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [data, setData] = useState<MainRowData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<DateRangeState[]>([
    {
      startDate: moment().startOf('day').toDate(),
      endDate: moment().endOf('day').toDate(),
      key: 'selection'
    }
  ]);
  const [dateRangeAnchorEl, setDateRangeAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [sortField, setSortField] = useState<SortField>('landed');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');

  const handleDateRangeClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setDateRangeAnchorEl(event.currentTarget);
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await dashboardService.getDashboardData({
        startDate: dateRange[0].startDate,
        endDate: dateRange[0].endDate
      });
      setData(response);
      setError(null);
    } catch (err) {
      setError('Failed to load dashboard data');
      console.error('Error fetching dashboard data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDateRangeClose = () => {
    setDateRangeAnchorEl(null);
  };

  const handleDateRangeChange = () => {
    handleDateRangeClose();
    fetchData()
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleRowClick = (rowName: string) => {
    const newExpandedRows = new Set(expandedRows);
    if (expandedRows.has(rowName)) {
      newExpandedRows.delete(rowName);
    } else {
      newExpandedRows.add(rowName);
    }
    setExpandedRows(newExpandedRows);
  };

  const handleSort = (field: SortField) => {
    const isAsc = sortField === field && sortOrder === 'asc';
    setSortOrder(isAsc ? 'desc' : 'asc');
    setSortField(field);
  };

  const sortApps = (apps: AppData[]) => {
    return [...apps].sort((a, b) => {
      const multiplier = sortOrder === 'asc' ? 1 : -1;
      switch (sortField) {
        case 'landed':
          return (a.landed - b.landed) * multiplier;
        case 'clickOut':
          return (a.clickOut - b.clickOut) * multiplier;
        case 'percentage':
          return (a.percentage - b.percentage) * multiplier;
        default:
          return 0;
      }
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" color="text.primary">
          Dashboard Overview
        </Typography>
        <Button
          variant="outlined"
          startIcon={<CalendarToday />}
          onClick={handleDateRangeClick}
          sx={{ color: 'text.primary', borderColor: 'text.primary' }}
        >
          {moment(dateRange[0].startDate).format('MMM D, YYYY')} - {moment(dateRange[0].endDate).format('MMM D, YYYY')}
        </Button>
      </Box>

      <Popover
        open={Boolean(dateRangeAnchorEl)}
        anchorEl={dateRangeAnchorEl}
        onClose={handleDateRangeClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <Box className="date-range-picker">
          <DateRange
            onChange={(item: any) => setDateRange([item.selection])}
            ranges={dateRange}
            rangeColors={[theme.palette.primary.main]}
          />
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end', backgroundColor: 'var(--background-color)' }}>
            <Button variant="contained" onClick={handleDateRangeChange} color="primary">
              Apply
            </Button>
          </Box>
        </Box>
      </Popover>

      <TableContainer component={Paper} elevation={3}>
        <Table>
          <TableBody>
            {data.map((row) => (
              <React.Fragment key={row.name}>
                <TableRow
                  sx={{
                    '& > *': { borderBottom: 'unset' },
                    '&:hover': {
                      backgroundColor: theme.palette.action.hover,
                    },
                  }}
                >
                  <TableCell>
                    <IconButton
                      aria-label="expand row"
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRowClick(row.name);
                      }}
                    >
                      {expandedRows.has(row.name) ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                    </IconButton>
                  </TableCell>
                  <TableCell component="th" scope="row">
                    <a
                      href={row.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        color: theme.palette.primary.main,
                        textDecoration: 'none'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
                      onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
                    >
                      {row.name}
                    </a>
                    <div style={{ color: 'gray' }}>{row.path}</div>
                  </TableCell>
                  <TableCell align="right">{row.totalLanded}</TableCell>
                  <TableCell align="right">{row.totalClickOut}</TableCell>
                  <TableCell align="right">{row.totalPercentage}%</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={5}>
                    <Collapse in={expandedRows.has(row.name)} timeout="auto" unmountOnExit>
                      <Box sx={{ margin: 1 }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>app</TableCell>
                              <TableCell align="right">
                                <TableSortLabel
                                  active={sortField === 'landed'}
                                  direction={sortField === 'landed' ? sortOrder : 'asc'}
                                  onClick={() => handleSort('landed')}
                                >
                                  landed #
                                </TableSortLabel>
                              </TableCell>
                              <TableCell align="right">
                                <TableSortLabel
                                  active={sortField === 'clickOut'}
                                  direction={sortField === 'clickOut' ? sortOrder : 'asc'}
                                  onClick={() => handleSort('clickOut')}
                                >
                                  click out #
                                </TableSortLabel>
                              </TableCell>
                              <TableCell align="right">
                                <TableSortLabel
                                  active={sortField === 'percentage'}
                                  direction={sortField === 'percentage' ? sortOrder : 'asc'}
                                  onClick={() => handleSort('percentage')}
                                >
                                  %
                                </TableSortLabel>
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {sortApps(row.apps).map((app) => (
                              <TableRow key={app.id}>
                                <TableCell component="th" scope="row">
                                    <div>{formatLongString(app.id)}</div>
                                </TableCell>
                                <TableCell align="right">{app.landed}</TableCell>
                                <TableCell align="right">{app.clickOut}</TableCell>
                                <TableCell align="right">{app.percentage}%</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default Dashboard; 